// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getFileUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import BannerFields from '../../fields/BannerFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
type $Props = $Translate;

type $State = {
  data: Array<$Model<'Banner'>>;
  loading: boolean;
};

const pageId = adminPages.adminBannersPage;

class AdminBannersPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.setupSocket();
    this.getData();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Banner') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminBanner();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'Banner'> = {
      ButtonClass: null,
      ButtonLink: null,
      ButtonName: null,
      ContainerClass: null,
      ImageLG: '',
      ImageMD: '',
      ImageSM: '',
      ImageXS: '',
      Name: '',
      Public: true,
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <BannerFields item={newItem} />
              )}
              id={'create-banner-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Banner'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertAdminBanner(params);
                },
              })}
              subTitle={t('New')}
              title={t('Banner')}
            >
              <Button
                color={'info'}
                id={'create-banner-button'}
                size={'sm'}
                title={t('Add new banner')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
            )}
          title={t('Banners')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'Banner'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <BannerFields item={dataItem} />
          )}
          id={`update-banner-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Banner'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminBanner(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Banner')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-banner-dialog-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-banner-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminBanner(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Banner')}
        >
          <Button
            color={'danger'}
            id={`delete-banner-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Name')}</th>
            <th>{t('Link')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Banner'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td className={'py-1'}>
                <img
                  alt={'Img'}
                  src={getFileUrl({
                    cdnUrl: config.CDN_URL,
                    file: dataItem.ImageLG,
                  })}
                  style={{
                    height: 32,
                  }}
                />
              </td>
              <td>{dataItem.Name}</td>
              <td>{dataItem.ButtonLink}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminBannersPageView;
